import { ExclamationCircleFilled, FileTextOutlined } from '@ant-design/icons';
import { InvoiceDetailCard, InvoicePaymentModal } from '@components';
import {
  useGenerateInvoice,
  useInvoiceByOrderId,
  useInvoiceTotals,
  usePaymentHandlers,
  useRelatedOrders,
  useAccess,
} from '@hooks';
import { isEstimateOut, isOrderLocked, OrderStatus } from '@models';
import { Order } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Button, Form, Modal, Tooltip, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useRef, useState } from 'react';

interface IProps {
  order: Order;
  iconOnly?: boolean;
}

export const OrderInvoiceCard: React.FC<IProps> = observer(
  ({ order, iconOnly }) => {
    const { invoiceStore, orderStore, serviceStore } = useStore();
    const { relatedCheckedIds: relatedCheck } = orderStore;

    const [paymentForm] = Form.useForm();
    const [modalType, setModalType] = useState<'payment' | 'refund' | null>(null);

    const { invoice } = useInvoiceByOrderId(order);
    const relatedOrdersHook = useRelatedOrders(order.id);

    const {
      invoiceTotal = 0,
      creditTotal = 0,
      refundTotal = 0,
      refundPaidTotal = 0,
      refundRequestedTotal = 0,
      paymentTotal = 0,
      dueTotal = 0,
      invoiceSubtotal = 0,
      invoiceVat = 0,
    } = useInvoiceTotals(invoice, invoice?.invoicePayments) || {};

    const {
      handlePaymentCancel,
      handlePartialPaymentInput,
      handlePaymentCreate,
      handlePosPayment,
      handleCashRefundCreate,
      handlePosRefund,
      resumeCardPayment,
    } = usePaymentHandlers({
      invoice,
      paymentForm,
      invoiceTotal,
      paymentTotal,
      dueTotal,
      setModalType,
    });

    const { order: access } = useAccess();
    const generatingRef = useRef(false);
    const generateInvoice = useGenerateInvoice();

    const handleGenerateInvoice = useCallback(async () => {
      if (!order || generatingRef.current) return;
      // The mileage update runs before the invoice request, so guard against a second click.
      generatingRef.current = true;

      try {
        const orders = relatedOrdersHook.relatedOrders.filter((x) => relatedCheck.includes(x.id));
        orders.push(order);

        const invoiceResult = await generateInvoice({
          order,
          orders,
          customerId: order.customerId,
          dueDate: 0,
        });

        // Stays on the job: its status now says it is invoiced, which makes the card load and show the invoice.
        if (invoiceResult) {
          // The card stays on the job and shows the invoice straight away, so it does not wait on the
          // status read-back below.
          await invoiceStore.get({ id: invoiceResult.id });
          // Every job on the invoice becomes invoiced, not only this one.
          await orderStore.refreshStatus(orders.map((x) => x.id));
        }
      } catch (error) {
        console.error('Error generating invoice:', error);
      } finally {
        generatingRef.current = false;
      }
    }, [order, relatedCheck, relatedOrdersHook, generateInvoice]);

    const handleConfirmGenerateInvoice = useCallback(() => {
      const completedRelatedOrders = relatedOrdersHook.relatedOrders.filter(
        (x) => x.status === OrderStatus.Completed
      );
      const unchecked = completedRelatedOrders.filter((x) => !relatedCheck.includes(x.id));

      if (completedRelatedOrders.length === 0 || unchecked.length === 0) {
        handleGenerateInvoice();
        return;
      }

      Modal.confirm({
        width: 480,
        title: 'Some related jobs are completed but not selected',
        icon: <ExclamationCircleFilled />,
        okText: 'Continue',
        cancelText: 'Cancel',
        content:
          'You have related items that are completed but not checked. Are you sure you want to continue?',
        onOk: handleGenerateInvoice,
      });
    }, [relatedOrdersHook.relatedOrders, relatedCheck, handleGenerateInvoice]);

    // Not once the invoice is on screen, even while the job's status has yet to catch up with it.
    const canGenerate = access.generateInvoice && order.status === OrderStatus.Completed && !invoice;
    // A job with no plate can still be invoiced, so this only says what the invoice will look like.
    const missingReg = !order.registrationNumber;
    const missingRegNote = 'No registration on this job - the invoice will be raised without one.';

    // Before the job is invoiced the card previews what the invoice will say - the live totals
    // of the services on screen - and why it cannot be raised yet, instead of a blank placeholder.
    // Only for statuses where no invoice is fetched; Ready/Paid keep the real card and its loading.
    const awaitingInvoice = !isOrderLocked(order.status);
    const { labourPrice, partsPrice, vatAmount, totalPrice } = serviceStore.pricingSummary;
    const reason =
      order.status === OrderStatus.Completed
        ? access.generateInvoice
          ? missingReg
            ? missingRegNote
            : 'Job complete - the invoice can be raised.'
          : 'Job complete - a manager can raise the invoice.'
        : isEstimateOut(order)
          ? 'Waiting for the customer to approve the estimate.'
          : order.status === OrderStatus.Canceled
            ? 'Cancelled jobs are not invoiced.'
            : 'Invoicing opens once the job is completed.';
    // What happens next, for a job that is still on its way to completion: the card is empty of an
    // invoice, so it says where one comes from. (Completed, cancelled: the reason above is the whole story.)
    const nextStep =
      order.status === OrderStatus.Completed || order.status === OrderStatus.Canceled
        ? undefined
        : 'Once the job is completed, a Generate invoice button appears here. The invoice is made from the services and parts on the job, and you can take payment.';
    const previewRow = (label: string, value: number, strong = false) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
        <Typography.Text type={strong ? undefined : 'secondary'} strong={strong}>
          {label}
        </Typography.Text>
        <Typography.Text strong={strong}>{Utils.formatCurrency(value)}</Typography.Text>
      </div>
    );
    const invoicePreview = (
      <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileTextOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
          <Typography.Text strong>Not invoiced yet</Typography.Text>
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {reason}
        </Typography.Text>
        <div
          style={{
            background: '#f7fafd',
            border: '1px solid #e8eef5',
            borderRadius: 8,
            padding: '8px 12px',
          }}
        >
          {previewRow('Labour', labourPrice)}
          {previewRow('Parts', partsPrice)}
          {previewRow('VAT', vatAmount)}
          {previewRow('Total', totalPrice, true)}
        </div>
      </div>
      {/* The foot of the card (it is as tall as the cards beside it): the button once there is one to
          press, until then a note on where the invoice comes from - so the gap is not just empty. */}
      {canGenerate ? (
        <Tooltip title={missingReg ? missingRegNote : undefined}>
          <span style={{ display: 'block' }}>
            <Button
              type="primary"
              block
              onClick={handleConfirmGenerateInvoice}
              disabled={invoiceStore.loading}
              loading={invoiceStore.loading}
            >
              Generate invoice
            </Button>
          </span>
        </Tooltip>
      ) : (
        nextStep && (
          <div style={{ padding: '0 4px', fontSize: 14, lineHeight: '22px', color: 'rgba(0, 0, 0, 0.45)' }}>
            {nextStep}
          </div>
        )
      )}
      </>
    );

    return (
      <>
        <InvoiceDetailCard
          invoice={invoice}
          emptyState={awaitingInvoice ? invoicePreview : undefined}
          invoiceSubtotal={invoiceSubtotal}
          invoiceVat={invoiceVat}
          invoiceTotal={invoiceTotal}
          creditTotal={creditTotal}
          refundTotal={refundTotal}
          refundPaidTotal={refundPaidTotal}
          refundRequestedTotal={refundRequestedTotal}
          paymentTotal={paymentTotal}
          dueTotal={dueTotal}
          onPayClick={() => setModalType('payment')}
        >
          {canGenerate && (
            <Tooltip title={missingReg ? missingRegNote : undefined}>
              <span style={{ display: 'block', width: iconOnly ? '100%' : 150 }}>
                <Button
                  type="primary"
                  block
                  onClick={handleConfirmGenerateInvoice}
                  disabled={invoiceStore.loading}
                  loading={invoiceStore.loading}
                >
                  Generate invoice
                </Button>
              </span>
            </Tooltip>
          )}
        </InvoiceDetailCard>

        <InvoicePaymentModal
          paymentForm={paymentForm}
          modalType={modalType}
          invoiceTotal={invoiceTotal}
          totalDue={dueTotal}
          paymentTotal={paymentTotal}
          handlePaymentCreate={handlePaymentCreate}
          handlePosPayment={handlePosPayment}
          handlePaymentCancel={handlePaymentCancel}
          handlePartialPaymentInput={handlePartialPaymentInput}
          handleCashRefundCreate={handleCashRefundCreate}
          handlePosRefund={handlePosRefund}
          resumeCardPayment={resumeCardPayment}
        />
      </>
    );
  }
);
